// Themed chat presets that mirror the three MCP Server Portals (Genomics /
// Pharma / Clinical). Selecting a theme enables that portal's full server
// roster in the chat and swaps the example prompts. The server→theme mapping
// lives on each CuratedServer (`theme`); this file holds the presentation +
// example prompts. Keep ids in sync with ServerTheme in curated-servers.ts.

import { CURATED_SERVERS } from "../../server/lib/curated-servers";

export type ChatThemeId = "genomics" | "pharma" | "clinical";

export interface ChatTheme {
  id: ChatThemeId;
  /** Full name, mirrors the portal. */
  name: string;
  /** Short label for chips/toggles. */
  short: string;
  /** One-line description. */
  blurb: string;
  /** Click-to-run example prompts for this domain. */
  prompts: string[];
}

export const CHAT_THEMES: ChatTheme[] = [
  {
    id: "genomics",
    name: "Genomics & Molecular",
    short: "Genomics",
    blurb:
      "Genes, variants, expression, protein structure, pathways, and phenotypes.",
    prompts: [
      "Map EGFR's relationship neighborhood — Open Targets associations, STRING interactions, and Reactome pathways, each tied to its source.",
      "For BRAF V600 in melanoma: pull gnomAD frequency + gene constraint, ClinVar significance, and the UniProt entry with any PDB structures.",
      "Top genetic drivers of ALS — cross-reference Open Targets associations, the GWAS Catalog, and Monarch phenotypes.",
    ],
  },
  {
    id: "pharma",
    name: "Drugs, Chemistry & Cancer",
    short: "Pharma",
    blurb:
      "Drug discovery, chemistry, oncology, trials, and the regulatory + safety picture.",
    prompts: [
      "Build a pipeline profile for osimertinib: ChEMBL mechanism + targets, ClinicalTrials.gov phases, OpenFDA approvals, and FAERS safety signals.",
      "For BRAF V600E melanoma, cross-reference CIViC therapies, DGIdb drug–gene interactions, and the FDA Orange Book.",
      "Compare KRAS G12C inhibitors — ChEMBL chemistry, cBioPortal mutation frequency, and active clinical trials.",
    ],
  },
  {
    id: "clinical",
    name: "Clinical, Healthcare & Literature",
    short: "Clinical",
    blurb:
      "Literature, evidence synthesis, clinical terminology, and healthcare data.",
    prompts: [
      "Run a mini systematic review on GLP-1 agonists in Alzheimer's across PubMed and ClinicalTrials.gov — return a PRISMA count and citations.",
      "Summarize the evidence and adverse-event signals for semaglutide: PubMed literature, OpenFDA labels, and FAERS reports.",
      "Map type 2 diabetes across ICD-10 codes and the clinical ontologies, with recent practice-guideline literature.",
    ],
  },
];

/**
 * Curated chat catalog grouped by `theme` — the single source of truth for the
 * landing-page "N databases" copy and the static OG/meta tags in index.html.
 * Cross-checked against cf/server/lib/curated/* by server-counts.test.ts, so
 * Counts are derived from the catalog; the test also checks static social tags.
 */
export const THEME_SERVER_COUNTS = CURATED_SERVERS.reduce<Record<ChatThemeId, number>>(
  (counts, server) => { counts[server.theme]++; return counts; },
  { genomics: 0, pharma: 0, clinical: 0 },
);

/** Total curated databases the chat exposes (sum of the per-theme counts). */
export const TOTAL_SERVER_COUNT: number = Object.values(
  THEME_SERVER_COUNTS,
).reduce((sum, n) => sum + n, 0);

export function getChatTheme(id: string | null | undefined): ChatTheme | undefined {
  return CHAT_THEMES.find((t) => t.id === id);
}
